import type {
  DraftNodeEnvelope,
  GeneratedNodePosition,
  GetProcessDraftResponse,
  GetProcessLayoutResponse,
  ProcessDraftStep
} from '@bliks-io/contracts';
import { reconcileEnvelope, removeDraftNodes } from '../../draftGraph/core';
import { reconcilePlaceholderInLayout } from './reconcilePlaceholder';

export interface BuildPlaceholderStepArgs {
  tempId: string;
  processId: string;
  draftId: string;
  laneKey?: string;
  inputs?: ProcessDraftStep['inputs'];
  task?: ProcessDraftStep['task'];
}

export function buildPlaceholderStep(args: BuildPlaceholderStepArgs): ProcessDraftStep {
  return {
    _id: args.tempId,
    draftId: args.draftId,
    processId: args.processId,
    stableStepId: args.tempId,
    actionType: 'task',
    whoElseDoesThis: [],
    inputs: args.inputs ?? [],
    updatedAt: new Date(),
    ...(args.task !== undefined ? { task: args.task } : {}),
    ...(args.laneKey ? { laneKey: args.laneKey } : {})
  } as unknown as ProcessDraftStep;
}

export function appendPlaceholderPosition(
  prev: GetProcessLayoutResponse,
  tempId: string,
  position: { x: number; y: number }
): GetProcessLayoutResponse {
  return {
    ...prev,
    stepPositions: [...prev.stepPositions, { stableStepId: tempId, x: position.x, y: position.y }]
  };
}

/**
 * Put one generated node's position into the cached layout, replacing any
 * entry under the same id. Client twin of the backend's
 * `replaceGeneratedNodePositionInLayout`.
 */
export function replaceGeneratedPosition(
  prev: GetProcessLayoutResponse,
  position: GeneratedNodePosition
): GetProcessLayoutResponse {
  return {
    ...prev,
    generatedNodePositions: [
      ...prev.generatedNodePositions.filter((g) => g.nodeId !== position.nodeId),
      position
    ]
  };
}

/**
 * Append server-minted layout rows to the cached draft layout, skipping ids the
 * cache already carries. Returns `prev` untouched when nothing is new, so the
 * cache keeps its identity.
 */
export function appendMissingPositions(
  prev: GetProcessLayoutResponse,
  positions: GetProcessLayoutResponse['stepPositions']
): GetProcessLayoutResponse {
  const existing = new Set(prev.stepPositions.map((p) => String(p.stableStepId)));
  const added = positions.filter((p) => !existing.has(String(p.stableStepId)));
  if (added.length === 0) return prev;
  return { ...prev, stepPositions: [...prev.stepPositions, ...added] };
}

/**
 * Reconcilers for a transaction that stages one placeholder node and whose
 * `persist` returns a `DraftNodeEnvelope` with the created node in
 * `createdNodeIds[0]`:
 *  - draft cache: drop the placeholder (and the temp-id edge refs it left on
 *    rewired nodes), then write every envelope node.
 *  - layout cache: move the placeholder's staged position onto the created
 *    node's real `stableStepId`, so the node doesn't flash through the `{0,0}`
 *    sentinel before the layout refetch.
 */
export function envelopePlaceholderReconcilers(tempId: string): {
  reconcile: (
    prev: GetProcessDraftResponse,
    envelope: DraftNodeEnvelope
  ) => GetProcessDraftResponse;
  reconcileLayout: (
    prev: GetProcessLayoutResponse,
    envelope: DraftNodeEnvelope
  ) => GetProcessLayoutResponse;
} {
  return {
    reconcile: (prev, envelope) =>
      reconcileEnvelope(removeDraftNodes(prev, new Set([tempId])), envelope),
    reconcileLayout: (prev, envelope) =>
      reconcilePlaceholderInLayout(prev, tempId, String(envelope.createdNodeIds[0]))
  };
}
